import type { AgentRunState, IdeaMaturity, WorkItemState, WorkOrderState } from "@szyos/contracts";
import { DomainError } from "./errors";

/* ---------- WorkOrder ---------- */

export const WORK_ORDER_TERMINAL: ReadonlySet<WorkOrderState> = new Set(["RELEASED", "FAILED", "CANCELLED"]);
export const WORK_ORDER_INTERRUPTS: ReadonlySet<WorkOrderState> = new Set([
  "WAITING_HUMAN",
  "BLOCKED",
  "PAUSED_PROVIDER",
  "SUSPECT",
]);

export type WorkOrderCommand =
  | { type: "submit_spec" }
  | { type: "approve_spec" }
  | { type: "request_spec_changes" }
  | { type: "approve_plan" }
  | { type: "request_plan_changes" }
  | { type: "start" }
  | { type: "items_done" }
  | { type: "verification_passed" }
  | { type: "verification_failed" }
  | { type: "accept" }
  | { type: "request_rework" }
  | { type: "deploy_staging" }
  | { type: "request_release" }
  | { type: "release" }
  | { type: "reopen_plan"; reason: string }
  | { type: "revise_spec"; reason: string }
  | { type: "wait_human"; reason: string }
  | { type: "block"; reason: string }
  | { type: "pause_provider"; reason: string }
  | { type: "suspect"; reason: string }
  | { type: "resume" }
  | { type: "fail"; reason: string }
  | { type: "cancel"; reason: string };

export interface WorkOrderStatus {
  state: WorkOrderState;
  resumeState: WorkOrderState | null;
  stateReason: string | null;
}

const NORMAL_PATH: Partial<Record<WorkOrderCommand["type"], [WorkOrderState, WorkOrderState]>> = {
  submit_spec: ["DRAFT", "SPEC_REVIEW"],
  approve_spec: ["SPEC_REVIEW", "PLAN_REVIEW"],
  request_spec_changes: ["SPEC_REVIEW", "DRAFT"],
  approve_plan: ["PLAN_REVIEW", "READY"],
  request_plan_changes: ["READY", "PLAN_REVIEW"],
  start: ["READY", "RUNNING"],
  items_done: ["RUNNING", "VERIFYING"],
  verification_passed: ["VERIFYING", "REVIEW_READY"],
  verification_failed: ["VERIFYING", "RUNNING"],
  accept: ["REVIEW_READY", "ACCEPTED"],
  request_rework: ["REVIEW_READY", "RUNNING"],
  deploy_staging: ["ACCEPTED", "STAGING"],
  request_release: ["STAGING", "RELEASE_APPROVAL"],
  release: ["RELEASE_APPROVAL", "RELEASED"],
};

const INTERRUPT_TARGET: Partial<Record<WorkOrderCommand["type"], WorkOrderState>> = {
  wait_human: "WAITING_HUMAN",
  block: "BLOCKED",
  pause_provider: "PAUSED_PROVIDER",
  suspect: "SUSPECT",
};

/** Aus diesen Zuständen führt eine geänderte Vorgabe zurück zur Freigabe des Briefings. */
const SPEC_REVISABLE: ReadonlySet<WorkOrderState> = new Set([
  "PLAN_REVIEW",
  "READY",
  "RUNNING",
  "VERIFYING",
  "WAITING_HUMAN",
  "BLOCKED",
  "PAUSED_PROVIDER",
]);

/** Aus diesen Zuständen führt „Noch anpassen“ zurück in die Planung (neue Plan-Revision nötig). */
const REOPENABLE: ReadonlySet<WorkOrderState> = new Set([
  "READY",
  "REVIEW_READY",
  "ACCEPTED",
  "STAGING",
  "RELEASE_APPROVAL",
]);

function invalid(from: WorkOrderState, cmd: WorkOrderCommand): never {
  throw new DomainError("INVALID_TRANSITION", `Übergang ${cmd.type} ist aus ${from} nicht erlaubt`, {
    from,
    command: cmd.type,
  });
}

/** Reine Zustandsmaschine des Auftrags (docs/03 §3). Rechte prüft der Aufrufer. */
export function transitionWorkOrder(current: WorkOrderStatus, cmd: WorkOrderCommand): WorkOrderStatus {
  const from = current.state;
  if (WORK_ORDER_TERMINAL.has(from)) invalid(from, cmd);

  const normal = NORMAL_PATH[cmd.type];
  if (normal) {
    if (from !== normal[0]) invalid(from, cmd);
    return { state: normal[1], resumeState: null, stateReason: null };
  }

  const interruptTarget = INTERRUPT_TARGET[cmd.type];
  if (interruptTarget) {
    if (WORK_ORDER_INTERRUPTS.has(from) || from === "DRAFT") invalid(from, cmd);
    return { state: interruptTarget, resumeState: from, stateReason: "reason" in cmd ? cmd.reason : null };
  }

  switch (cmd.type) {
    case "reopen_plan":
      if (!REOPENABLE.has(from)) invalid(from, cmd);
      return { state: "PLAN_REVIEW", resumeState: null, stateReason: cmd.reason };
    case "revise_spec":
      // Geänderte Vorgabe (etwa aus taskFLOW): neues Briefing zur Freigabe, danach ein neuer Plan.
      if (!SPEC_REVISABLE.has(from)) invalid(from, cmd);
      return { state: "SPEC_REVIEW", resumeState: null, stateReason: cmd.reason };
    case "resume":
      if (!WORK_ORDER_INTERRUPTS.has(from) || !current.resumeState) invalid(from, cmd);
      return { state: current.resumeState, resumeState: null, stateReason: null };
    case "fail":
      return { state: "FAILED", resumeState: null, stateReason: cmd.reason };
    case "cancel":
      return { state: "CANCELLED", resumeState: null, stateReason: cmd.reason };
    default:
      return invalid(from, cmd);
  }
}

export type Phase = "briefing" | "planung" | "umsetzung" | "review" | "release";
export const PHASES: readonly { key: Phase; label: string }[] = [
  { key: "briefing", label: "Briefing" },
  { key: "planung", label: "Planung" },
  { key: "umsetzung", label: "Umsetzung" },
  { key: "review", label: "Review" },
  { key: "release", label: "Release" },
];

const PHASE_OF: Partial<Record<WorkOrderState, Phase>> = {
  DRAFT: "briefing",
  SPEC_REVIEW: "briefing",
  PLAN_REVIEW: "planung",
  READY: "planung",
  RUNNING: "umsetzung",
  VERIFYING: "umsetzung",
  REVIEW_READY: "review",
  ACCEPTED: "review",
  STAGING: "release",
  RELEASE_APPROVAL: "release",
  RELEASED: "release",
};

/** Ordnet einen Zustand einer der fünf Phasen des Steppers zu (docs/06 §2). */
export function phaseOf(status: Pick<WorkOrderStatus, "state" | "resumeState">): Phase {
  const effective = WORK_ORDER_INTERRUPTS.has(status.state) ? status.resumeState : status.state;
  return (effective && PHASE_OF[effective]) ?? "briefing";
}

/* ---------- AgentRun ---------- */

export type RunCommand =
  | { type: "claim" }
  | { type: "start" }
  | { type: "complete" }
  | { type: "fail" }
  | { type: "cancel" }
  | { type: "lose_heartbeat" }
  | { type: "requeue" };

export const RUN_TERMINAL: ReadonlySet<AgentRunState> = new Set(["COMPLETED", "FAILED", "CANCELLED"]);

const RUN_TRANSITIONS: Record<RunCommand["type"], [readonly AgentRunState[], AgentRunState]> = {
  claim: [["QUEUED"], "LEASED"],
  start: [["LEASED"], "RUNNING"],
  complete: [["RUNNING"], "COMPLETED"],
  fail: [["LEASED", "RUNNING", "SUSPECT"], "FAILED"],
  cancel: [["QUEUED", "LEASED", "RUNNING", "SUSPECT"], "CANCELLED"],
  lose_heartbeat: [["LEASED", "RUNNING"], "SUSPECT"],
  requeue: [["SUSPECT"], "QUEUED"],
};

export function transitionRun(from: AgentRunState, cmd: RunCommand): AgentRunState {
  const [allowed, to] = RUN_TRANSITIONS[cmd.type];
  if (!allowed.includes(from)) {
    throw new DomainError("INVALID_TRANSITION", `Lauf: ${cmd.type} ist aus ${from} nicht erlaubt`, {
      from,
      command: cmd.type,
    });
  }
  return to;
}

/* ---------- Idea ---------- */

export type IdeaCommand = "clarify" | "mark_ready" | "park" | "discard" | "restore" | "promote";

const IDEA_TRANSITIONS: Record<IdeaCommand, [readonly IdeaMaturity[], IdeaMaturity]> = {
  clarify: [["RAW"], "CLARIFIED"],
  mark_ready: [["RAW", "CLARIFIED"], "READY"],
  park: [["RAW", "CLARIFIED", "READY"], "PARKED"],
  discard: [["RAW", "CLARIFIED", "READY", "PARKED"], "DISCARDED"],
  restore: [["PARKED", "DISCARDED"], "RAW"],
  promote: [["RAW", "CLARIFIED", "READY"], "PROMOTED"],
};

export function transitionIdea(from: IdeaMaturity, cmd: IdeaCommand): IdeaMaturity {
  const [allowed, to] = IDEA_TRANSITIONS[cmd];
  if (!allowed.includes(from)) {
    throw new DomainError("INVALID_TRANSITION", `Idee: ${cmd} ist aus ${from} nicht erlaubt`, {
      from,
      command: cmd,
    });
  }
  return to;
}

/** Titel, Text und Schlagworte einer Idee lassen sich ändern, solange sie weder Projekt noch verworfen ist. */
export const EDITABLE_IDEA_MATURITIES: readonly IdeaMaturity[] = ["RAW", "CLARIFIED", "READY", "PARKED"];

export function isIdeaEditable(maturity: IdeaMaturity): boolean {
  return EDITABLE_IDEA_MATURITIES.includes(maturity);
}

/* ---------- WorkItem ---------- */

export type WorkItemCommand =
  "start" | "complete" | "verify_start" | "verify_pass" | "fail" | "block" | "unblock" | "retry" | "waive";

const ITEM_TRANSITIONS: Record<WorkItemCommand, [readonly WorkItemState[], WorkItemState]> = {
  start: [["not_started"], "running"],
  complete: [["running"], "unverified"],
  verify_start: [["unverified"], "verifying"],
  verify_pass: [["verifying"], "verified"],
  fail: [["running", "verifying", "unverified"], "failed"],
  block: [["not_started", "running"], "blocked"],
  unblock: [["blocked"], "not_started"],
  retry: [["failed"], "not_started"],
  waive: [["not_started", "failed", "blocked", "unverified"], "waived_by_human"],
};

export function transitionWorkItem(from: WorkItemState, cmd: WorkItemCommand): WorkItemState {
  const [allowed, to] = ITEM_TRANSITIONS[cmd];
  if (!allowed.includes(from)) {
    throw new DomainError("INVALID_TRANSITION", `WorkItem: ${cmd} ist aus ${from} nicht erlaubt`, {
      from,
      command: cmd,
    });
  }
  return to;
}

export const ITEM_DONE: ReadonlySet<WorkItemState> = new Set(["verified", "waived_by_human"]);
